import {
    TraceWidget,
    TraceWidgetColumnName,
    TraceWidgetColumns,
    TraceWidgetVisualization,
    Widget
} from "../internalwidgets";
import {GridStack} from "gridstack";
import React, {Dispatch, SetStateAction, useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {determineFullWidth, findParentGrid} from "../utils/grid";
import {cn, useDebounce} from "../../ui/lib/utils";
import {ButtonWithTooltip} from "../Dashboard";
import {CopyIcon, XIcon} from "lucide-react";
import {cloneWidget, removeWidget} from "../utils";
import {MdModeEdit} from "react-icons/md";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter} from "../../ui/dialog";
import {Button} from "../../ui/button";
import Traces, {getTraces, TracePanel, TracesComponent} from "../../../pages/Traces"
import axios from "../../../utility/customAxios";
import {Checkbox} from "../../ui/checkbox";
import {Label} from "../../ui/label";
import {Input} from "../../ui/input";
import {useSelector} from "react-redux";
import timerange from "../../../store/reducers/timerange";
import {DateTimePickerRange} from "../../ui/DateTimeSelector";
import {useSearchParams} from "react-router-dom";
import {useDebouncedCallback} from "use-debounce";
import {Attribute} from "../../../types/telemetry";
import {MultiSelectorInputTags} from "../../Input/MultiSelectorInputTags/MultiSelectorInputTags";
import {
    DropDownItem, dropDownItemArrayToStringArray,
    MultiSelectorDropDown,
    stringArrayToDropDownItemArray
} from "../../Input/MultiSelectorDropdown/MultiSelectorDropDown";

export const EmbeddedTraceWidget = (props: {
    editable: boolean,
    id?: string,
    widget: TraceWidget,
    grids: Map<string, GridStack>,
    setGrids: Dispatch<SetStateAction<Map<string, GridStack>>>,
    widgets: Map<string, Widget>,
    setWidgets: Dispatch<SetStateAction<Map<string, Widget>>>,
    updateParentOfMount?: (id: string) => void
    ref?: React.MutableRefObject<any>
    setRootChildren: Dispatch<SetStateAction<Widget[]>>
}) => {
    const [id] = React.useState<string>(props.id ?? "id-" + uuidv4())
    const [widget, setWidget] = React.useState<TraceWidget>(props.widget)
    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
    const [isFullWidth, setIsFullWidth] = useState(false);
    const dialogRef = React.useRef<HTMLDivElement>(null);

    // Add title state
    const [title, setTitle] = useState<string>(props.widget.title || "");

    // Add state for visualization options
    const [hideSearchBar, setHideSearchBar] = useState<boolean>(props.widget.visualization?.hideSearchBar || false);
    const [visibleColumns, setVisibleColumns] = useState<string[]>(props.widget.visualization?.visibleColumns || TraceWidgetColumns);

    const handleClose = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmClose = () => {
        setShowConfirmDialog(false);
        setIsDialogOpen(false);
    };

    const handleCancelClose = () => {
        setShowConfirmDialog(false);
    };

    // Traces state
    const [traces, setTraces] = useState<Traces>({traces: [], filterAttributes: new Map()});
    const [isLoadingTraces, setIsLoadingTraces] = useState<boolean>(true);
    const [filter, setFilter] = useState<Map<string, string[]>>(props.widget.filters || new Map());
    const [excludeFilter, setExcludeFilter] = useState<Map<string, string[]>>(props.widget.excludeFilters || new Map());
    const [regexes, setRegexes] = useState<string[]>(props.widget.regexes || []);
    const [excludeRegexes, setExcludeRegexes] = useState<string[]>(props.widget.excludeRegexes || []);
    const [ascending, setAscending] = useState<boolean>(false);
    const debouncedAscending = useDebounce(ascending, 100);
    const [prevMaxTime, setPrevMaxTime] = useState<number | undefined>(undefined);
    const debouncedGetTraces = useDebouncedCallback(getTraces, 10);
    const [getTracesAbortController, setGetTracesAbortController] = useState(new AbortController());

    // Get timeRange from Redux
    const timeRange = useSelector(timerange.selectors.getTimeRange)

    // Get environment from URL parameters
    const [searchParams, setSearchParams] = useSearchParams();
    const [environments, setEnvironments] = useState<string[]>([]);

    // Update environment when URL parameters change
    useEffect(() => {
        let environments = searchParams.get("environment");
        if (environments !== null) {
            setEnvironments([environments])
        }
    }, [searchParams])

    // Get all the log attributes to filter by. This is important for the dropdown to open on click.
    useEffect(() => {
        axios.get("/api/v1/tracesSummaryAttributes").then((response) => {
            let attributes = response.data.attributes;
            setTraces(prevState => {
                let newMap = new Map<string, Attribute[]>(prevState.filterAttributes);
                for (let attribute of attributes) {
                    if (!newMap.has(attribute)) {
                        newMap.set(attribute, [])
                    }
                }
                return {...prevState, filterAttributes: newMap}
            })
        }).catch((e) => {
            if (e.code === "ERR_CANCELED") {
                return;
            } else {
                console.error(e);
            }
        })
    }, [])


    // Reset prevMaxTime when filter changes, time range changes, or environment changes
    useEffect(() => {
        setPrevMaxTime(undefined);
    }, [filter, excludeFilter, regexes, excludeRegexes, ascending, timeRange, environments]);

    useEffect(() => {
        // Get Traces
        debouncedGetTraces(setIsLoadingTraces, filter, timeRange, excludeFilter, regexes, excludeRegexes, prevMaxTime, debouncedAscending, environments, setTraces, getTracesAbortController, setGetTracesAbortController);
    }, [prevMaxTime, debouncedAscending, filter, excludeFilter, regexes, excludeRegexes, timeRange, environments]);

    useEffect(() => {
        props.setWidgets((prev) => {
            let newWidgets = new Map<string, Widget>(prev)
            newWidgets.set(id, props.widget)
            return newWidgets
        })

        if (props.updateParentOfMount !== undefined) {
            props.updateParentOfMount(id)
        }
    }, [])

    useEffect(() => {
        const {element, grid} = findParentGrid(id, props.grids);
        const fullWidth = determineFullWidth(element, grid, props.widget.position ?? {});
        setIsFullWidth(fullWidth);
    }, [id, props.grids, props.widget.position, props]);

    let leftMargin = "pl-1"
    if (props.widget.position?.x === 0) {
        leftMargin = "pl-2"
    }
    let rightMargin = "pr-1"
    if (isFullWidth) {
        rightMargin = "pr-2"
    }
    let topMargin = "pt-1"
    if (props.widget.position?.y === 0) {
        topMargin = "pt-2"
    }
    let bottomMargin = "pb-1"

    return (
        <div className={cn(id, "flex flex-col grow")}>
            <div className={cn("flex grow shrink max-h-full", leftMargin, topMargin, rightMargin, bottomMargin)}>
                <div className={cn("border flex flex-col grow h-full rounded max-w-full bg-backgroundmedium")}>
                    <div
                        className={"flex draggablehandle justify-between px-2 h-8 max-w-full bg-backgroundmedium"}>
                        <div className={"flex flex-col justify-center truncate"}>
                            {widget.title && (
                                <span className="text-textmedium font-semibold truncate">
                                    {widget.title}
                                </span>
                            )}
                        </div>
                        <div className={"flex gap-1"}>
                            {props.editable &&
                                <div className={"flex flex-col justify-center"}>
                                    <ButtonWithTooltip
                                        icon={CopyIcon}
                                        tooltipText="Duplicate widget"
                                        onClick={() => {
                                            cloneWidget(widget, props.setRootChildren)
                                        }}
                                    />
                                </div>
                            }
                            {props.editable && (
                                <>
                                    <div className={"flex flex-col items-center justify-center"}>
                                        <ButtonWithTooltip
                                            icon={MdModeEdit}
                                            tooltipText="Edit widget"
                                            onClick={() => setIsDialogOpen(true)}
                                        />
                                    </div>
                                    <Dialog open={isDialogOpen} modal={false}>
                                        <DialogContent
                                            ref={dialogRef}
                                            onInteractOutside={(e) => {
                                                // Prevent dialog from closing when interacting with any popover or dropdown elements
                                                e.preventDefault();
                                            }}
                                            onAbort={() => setIsDialogOpen(false)}
                                            onFocusOutside={(e) => {
                                                // Prevent dialog from closing when focus moves to any popover or dropdown elements
                                                e.preventDefault();
                                            }}
                                            className={"w-screen h-screen m-0 p-0 bg-black/75 flex items-center justify-center border-none"}
                                        >
                                            <div
                                                className={"border border-border rounded p-0 bg-background max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] m-0 flex flex-col overflow-hidden"}>

                                                <div className={"flex-none flex justify-between border-b h-[32px]"}>
                                                    <div
                                                        className={"flex flex-col justify-center text-textdark ml-2 items-center text-center"}>
                                                        Trace Widget Builder
                                                    </div>
                                                    <div className={"flex"}>
                                                        <DateTimePickerRange/>
                                                        <div
                                                            className={"text-textmedium border border-red-500 bg-red-500/20 rounded flex items-center justify-center pl-1 m-1 hover:cursor-pointer hover:text-textlight"}
                                                            onClick={handleClose}
                                                        >
                                                            Cancel <XIcon className={"w-5 h-5"}/>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div
                                                    className={"px-2 flex grow shrink min-h-0 min-w-0 flex-col gap-4 overflow-y-auto pb-16 mt-4"}>
                                                    {/* Step 1: Title (Optional) */}
                                                    <div className={"flex flex-col gap-4 flex-shrink-0"}>
                                                        <div className={"flex justify-start gap-2"}>
                                                            <div
                                                                className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                                                                1
                                                            </div>
                                                            <div
                                                                className={"text-lg flex gap-1 justify-center text-center text-textlight"}>
                                                                Title <span
                                                                className={"text-textdark"}>(optional)</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <Input
                                                                type="text"
                                                                placeholder="Enter widget title"
                                                                value={title}
                                                                onChange={(e) => setTitle(e.target.value)}
                                                                className="text-base h-10 border focus-visible:ring-0 bg-transparent focus:bg-backgroundlight"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Step 2: Select Log Data */}
                                                    <div className={"flex flex-col gap-4 flex-1 min-h-0"}>
                                                        <div className={"flex justify-start gap-2 flex-shrink-0"}>
                                                            <div
                                                                className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                                                                2
                                                            </div>
                                                            <div
                                                                className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                                                                Select Data
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-1 min-h-0 w-full overflow-hidden">
                                                            <div className="w-full h-full flex">
                                                                <TracesComponent
                                                                    noBaseView={true}
                                                                    filter={filter}
                                                                    setFilter={setFilter}
                                                                    excludeFilter={excludeFilter}
                                                                    setExcludeFilter={setExcludeFilter}
                                                                    regexes={regexes}
                                                                    setRegexes={setRegexes}
                                                                    excludeRegexes={excludeRegexes}
                                                                    setExcludeRegexes={setExcludeRegexes}
                                                                    ascending={ascending}
                                                                    setAscending={setAscending}
                                                                    dialogRef={dialogRef}
                                                                    visibleColumns={visibleColumns}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Step 3: Visualization Options */}
                                                    <div className={"flex flex-col gap-4 flex-shrink-0"}>
                                                        <div className={"flex justify-start gap-2"}>
                                                            <div
                                                                className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                                                                3
                                                            </div>
                                                            <div
                                                                className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                                                                Visualization Options
                                                            </div>
                                                        </div>
                                                        <div className="border rounded p-4 bg-backgroundmedium">
                                                            <div className="flex flex-col space-y-4">
                                                                <div
                                                                    className="flex items-center justify-start space-x-2">
                                                                    <Checkbox
                                                                        id="hideSearchBar"
                                                                        checked={hideSearchBar}
                                                                        onCheckedChange={(checked) => setHideSearchBar(checked === true)}
                                                                    />
                                                                    <Label
                                                                        htmlFor="hideSearchBar"
                                                                        className="text-textmedium cursor-pointer"
                                                                    >
                                                                        Hide search bar in widget
                                                                    </Label>
                                                                </div>
                                                                <TraceWidgetVisualizationSettings
                                                                    setVisibleColumns={setVisibleColumns}
                                                                    visibleColumns={visibleColumns}/>
                                                            </div>

                                                        </div>
                                                    </div>
                                                    <div
                                                        className="relative bottom-0 left-0 right-0">
                                                        <Button
                                                            onClick={() => {
                                                                // Update the widget with the current filter settings
                                                                const updatedWidget: TraceWidget = {
                                                                    ...props.widget,
                                                                    title: title,
                                                                    filters: filter,
                                                                    excludeFilters: excludeFilter,
                                                                    regexes: regexes,
                                                                    excludeRegexes: excludeRegexes,
                                                                    visualization: {
                                                                        ...props.widget.visualization,
                                                                        hideSearchBar: hideSearchBar,
                                                                        visibleColumns: visibleColumns,
                                                                    }
                                                                };

                                                                // Update the widget in the parent component
                                                                props.setWidgets((prev) => {
                                                                    let newWidgets = new Map<string, Widget>(prev);
                                                                    newWidgets.set(id, updatedWidget);
                                                                    return newWidgets;
                                                                });

                                                                // Update the root children if needed
                                                                props.setRootChildren((prev) => {
                                                                    return prev.map((w) => {
                                                                        if (w === props.widget) {
                                                                            return updatedWidget;
                                                                        }
                                                                        return w;
                                                                    });
                                                                });

                                                                // Update the local state
                                                                setWidget(updatedWidget);

                                                                // Close the dialog
                                                                setIsDialogOpen(false);
                                                            }}
                                                            className={"px-2 bg-primarytransparent border border-primary w-full text-textmedium hover:cursor-pointer hover:text-textlight"}>Save</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>

                                    {/* Confirmation dialog */}
                                    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                                        <DialogContent className="sm:max-w-[525px] bg-backgroundmedium">
                                            <DialogHeader>
                                                <DialogTitle>Close Editor?</DialogTitle>
                                                <DialogDescription className={"text-textmedium"}>
                                                    Are you sure you want to close the editor? Any changes will be
                                                    discarded.
                                                </DialogDescription>
                                            </DialogHeader>
                                            <DialogFooter>
                                                <Button onClick={handleCancelClose}
                                                        className={"text-textmedium border rounded border-primary bg-primarytransparent"}>
                                                    Back to Editor
                                                </Button>
                                                <Button onClick={handleConfirmClose}
                                                        className={"text-textmedium border rounded border-amber-500 bg-amber-500/10"}>
                                                    Close Without Saving
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                    <div className={"flex flex-col items-center justify-center"}>
                                        <ButtonWithTooltip
                                            icon={XIcon}
                                            tooltipText="Delete widget"
                                            onClick={() => removeWidget(id, props.grids, props.setGrids, props.widgets, props.setWidgets)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={"flex flex-col grow shrink min-h-0"}>
                        <div className={"flex grow shrink min-h-0"}>
                            <TracePanel
                                ascending={ascending}
                                setAscending={setAscending}
                                traces={traces}
                                filter={filter}
                                setFilter={setFilter}
                                excludeFilter={excludeFilter}
                                setExcludeFilter={setExcludeFilter}
                                regexes={regexes}
                                setRegexes={setRegexes}
                                excludeRegexes={excludeRegexes}
                                setExcludeRegexes={setExcludeRegexes}
                                scrolledToBottomHandler={() => {
                                    if (traces.traces.length === 0) {
                                        return;
                                    }
                                    let newPrevMaxTime = traces.traces[traces.traces.length - 1].time + 1;
                                    setPrevMaxTime(newPrevMaxTime);
                                }}
                                isFilterShown={true}
                                enableDashboardStyle={true}
                                hideSearchBar={hideSearchBar}
                                visibleColumns={visibleColumns}
                                dropDownFunction={(key: string) => {
                                    const filters = Object.fromEntries(filter);
                                    const excludeFilters = Object.fromEntries(excludeFilter);
                                    // Use the timeRange from Redux
                                    return axios.post("/api/v1/tracesSummaryIndividualAttribute", {
                                        "startTime": Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                                        "endTime": Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
                                        // Removed because we want to keep everything for now
                                        "excludeFilters": excludeFilters,
                                        // Removed because we want to keep everything for now
                                        "filters": filters,
                                        // Removed because we want to keep everything for now
                                        "regexes": regexes,
                                        // Removed because we want to keep everything for now
                                        "excludeRegexes": excludeRegexes,
                                        "environments": environments[0] === "" ? [] : environments,
                                        "attribute": key
                                    }).then((response) => {
                                        return response.data.attribute;
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


function TraceWidgetVisualizationSettings(props: {
    visibleColumns: string[]
    setVisibleColumns: Dispatch<SetStateAction<string[]>>;
}) {
    return <div className={"flex text-textmedium w-full"}>
        <MultiSelectorDropDown
            allSelector={"All Columns"}
            selectorSingular="Visible Column"
            possibleItems={stringArrayToDropDownItemArray(TraceWidgetColumns)}
            selectedItems={stringArrayToDropDownItemArray(props.visibleColumns)}
            setSelectedItems={(selectedItems) => {
                props.setVisibleColumns(dropDownItemArrayToStringArray(selectedItems))
            }}
            isSingleSelect={false}
        /></div>
}
